import { useMemo } from 'react';
import './FundChart.css';
import PeriodSelector, { ChartPeriod, PeriodOption } from './PeriodSelector';
import FundLineChart, { getFundChartColors } from './FundLineChart';
import ChartLegend, { LegendItem } from './ChartLegend';
import Loading from '../../../../Components/Loading/Loading';
import { ThemeType } from '../../../../utils/type';
import {
  FundChartPeriod,
  FundChartsData,
} from '../../../../types/fundsummary.type';

export interface FundChartProps {
  data?: FundChartsData;
  periodOptions?: PeriodOption[];
  theme?: ThemeType;
  period: FundChartPeriod;
  isLoading?: boolean;
  onPeriodChange?: (period: ChartPeriod) => void;
}

export const PERIOD_OPTIONS: PeriodOption[] = [
  { label: '3M', value: '3M' },
  { label: '6M', value: '6M' },
  { label: '1Y', value: '1Y' },
  { label: '3Y', value: '3Y' },
  { label: '5Y', value: '5Y' },
  { label: 'All', value: 'All' },
];

const FundChart = ({
  periodOptions = PERIOD_OPTIONS,
  theme,
  period,
  data,
  isLoading = false,
  onPeriodChange,
}: FundChartProps): JSX.Element => {
  const handleChange = (next: ChartPeriod) => {
    onPeriodChange?.(next);
  };

  const legendItems = useMemo<LegendItem[]>(() => {
    const colors = getFundChartColors(theme);
    const items: LegendItem[] = [];

    if ((data?.navChart?.data?.length ?? 0) > 0) {
      items.push({
        label: 'NAV (Net Asset Value)',
        color: colors.nav,
        marker: 'line',
      });
    }
    if ((data?.midChart?.data?.length ?? 0) > 0) {
      items.push({
        label: 'Benchmark',
        color: colors.benchmark,
        marker: 'line',
      });
    }
    if ((data?.tnaChart?.data?.length ?? 0) > 0) {
      items.push({
        label: 'TNA (Total Net Asset)',
        color: colors.tna,
        marker: 'box',
      });
    }

    return items;
  }, [theme, data]);

  return (
    <div className="fund-chart">
      <PeriodSelector
        options={periodOptions}
        value={period}
        onChange={handleChange}
      />
      <div className="fund-chart__plot">
        <FundLineChart data={data} theme={theme} activePeriod={period} />
      </div>
      {legendItems.length > 0 && <ChartLegend items={legendItems} />}
    </div>
  );
};

export default FundChart;
